import {
  AssessmentLevelNamesType,
  AssessmentQuestion,
  AssessmentSection,
  AssessmentType,
} from '@app/src/types/resourceExplorer'
import { useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { AssessmentTemplateFormValues } from '../CreateAssessmentFromQuestionnaire'
import { ClassificationRulesFormData } from '../EditQuestionClassificationDialog'
import { formatQuestionForAssessment, formatSectionsForAssessment } from '../utils/dataTransformers'
import { AssessmentFormattedQuestion, EnhancedAssessmentFormQuestion } from '../utils/types'

interface UseAssessmentFormProps {
  sections: AssessmentSection[]
}

export const useAssessmentForm = ({ sections }: UseAssessmentFormProps) => {
  const { watch, setValue } = useFormContext<AssessmentTemplateFormValues>()

  const assessmentType = watch('assessmentType')
  const levelNamesType = watch('levelNamesType')
  const currentSections = watch('sections') || []

  const findExistingQuestionData = useCallback(
    (questionId: number): AssessmentFormattedQuestion | null => {
      for (const section of currentSections) {
        const foundQuestion = section.questions.find(q => q.questionId === questionId)
        if (foundQuestion) {
          return foundQuestion
        }
      }
      return null
    },
    [currentSections],
  )

  const createEnhancedQuestion = useCallback(
    (question: AssessmentQuestion): EnhancedAssessmentFormQuestion => {
      let existingQuestionData: AssessmentFormattedQuestion | null = null

      if (assessmentType === AssessmentType.Flexible) {
        existingQuestionData = findExistingQuestionData(question.questionId) || formatQuestionForAssessment(question)
      }

      return {
        ...question.question,
        assessmentData: existingQuestionData || undefined,
      }
    },
    [assessmentType, findExistingQuestionData],
  )

  const handleClassificationSave = useCallback(
    (questionId: number, classificationData: ClassificationRulesFormData) => {
      const updatedSections = currentSections.map(section => ({
        ...section,
        questions: section.questions.map(question => {
          if (question.questionId === questionId) {
            return {
              ...question,
              impactValueType: classificationData.impactValueType,
              points: classificationData.points,
            }
          }
          return question
        }),
      }))

      setValue('sections', updatedSections)
    },
    [currentSections, setValue],
  )

  const initializeAssessmentFormatting = useCallback(() => {
    if (assessmentType === AssessmentType.Flexible && sections.length > 0 && currentSections.length === 0) {
      const formattedSections = formatSectionsForAssessment(sections)
      setValue('sections', formattedSections)
    } else if (assessmentType !== AssessmentType.Flexible) {
      setValue('sections', [])
      setValue('levelNamesType', AssessmentLevelNamesType.Intervals)
    }
  }, [assessmentType, sections, currentSections.length, setValue])

  const getFormPointsForQuestion = (questionId: number, sectionIndex?: number) => {
    if (assessmentType === AssessmentType.Flexible && currentSections?.length > 0) {
      for (const section of currentSections) {
        const foundQuestion = section.questions.find(q => q.questionId === questionId)
        if (foundQuestion && foundQuestion.points) {
          return foundQuestion.points
        }
      }
    }

    const originalSection =
      sectionIndex !== undefined
        ? sections[sectionIndex]
        : sections.find(s => s.questions.some(q => q.questionId === questionId))
    const originalQuestion = originalSection?.questions.find(q => q.questionId === questionId)
    return originalQuestion?.points
  }

  return {
    assessmentType,
    levelNamesType,
    currentSections,
    createEnhancedQuestion,
    handleClassificationSave,
    initializeAssessmentFormatting,
    getFormPointsForQuestion,
  }
}
